import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './auth.module.css';
import { EyeIcon, EyeOffIcon, ArrowLeftIcon } from './icons';
import OtpInput from './OtpInput';
import { useAuth, AuthMode } from '../../store/AuthContext';
import {
  signupWithEmail,
  verifyEmail,
  resendVerificationCode,
  requestForgotPassword,
  confirmForgotPassword,
  validateSignupPassword,
  SignupConflictError,
} from '../../services/authService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type View = 'email' | 'password' | 'verify' | 'forgot' | 'confirm';

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

interface AuthFlowProps {
  /** Initial mode when nothing has requested otherwise. */
  defaultMode?: AuthMode;
}

/**
 * The full auth flow, rendered inline inside the hero login card (no popups):
 * login, signup → email verification, and forgot → reset password. On success it
 * hands the JWT to the app via AuthContext.completeAuth / loginWithEmailAction.
 */
const AuthFlow: React.FC<AuthFlowProps> = ({ defaultMode = 'signup' }) => {
  const { authIntent, clearAuthIntent, loginWithEmailAction, completeAuth } = useAuth();

  const [mode, setMode] = useState<AuthMode>(defaultMode);
  const [view, setView] = useState<View>('email');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const [pendingCreds, setPendingCreds] = useState<{ email: string; password: string } | null>(null);
  const [emailForReset, setEmailForReset] = useState('');

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const resetToEmail = useCallback((nextMode: AuthMode) => {
    setMode(nextMode);
    setView('email');
    setPassword('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowNewPassword(false);
    setError('');
    setSuccess('');
    setLoading(false);
  }, []);

  // The navbar (or any page) can ask the card to open in a given mode.
  useEffect(() => {
    if (!authIntent) return;
    resetToEmail(authIntent);
    clearAuthIntent();
  }, [authIntent, clearAuthIntent, resetToEmail]);

  // Countdown for the verify / reset steps.
  useEffect(() => {
    if ((view !== 'verify' && view !== 'confirm') || countdown <= 0) return;
    const t = setInterval(() => setCountdown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [view, countdown]);

  /* ── handlers ── */

  const handleEmailNext = () => {
    const trimmed = email.trim();
    if (!trimmed) return setError('Please enter your email');
    if (!EMAIL_RE.test(trimmed)) return setError('Please enter a valid email');
    clearMessages();
    setView('password');
  };

  const handleSignin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!password) return setError('Please enter your password');
    setLoading(true);
    clearMessages();
    try {
      await loginWithEmailAction({ email: email.trim(), password });
      setSuccess('Successfully signed in'); // redirect to the app is in flight
    } catch {
      setLoading(false);
      setError('Incorrect password. Please try again.');
    }
  };

  const handleSignup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const validation = validateSignupPassword(password);
    if (!validation.valid) return setError(validation.message);
    setLoading(true);
    clearMessages();
    try {
      await signupWithEmail({ email: email.trim(), password });
      setPendingCreds({ email: email.trim(), password });
      setCode('');
      setCountdown(60);
      setView('verify');
    } catch (err) {
      // An address that is already taken is not a failure to report and stop
      // at — it is a fork, and the two sides need opposite things.
      if (err instanceof SignupConflictError && err.state === 'unconfirmed') {
        // Signup was started before but the code was never entered. The
        // backend has already sent a fresh one, so go straight to the code
        // screen. Sending this user to "forgot password" instead is a dead
        // end: Cognito refuses a reset while the email is unverified.
        setPendingCreds({ email: email.trim(), password });
        setCode('');
        setCountdown(60);
        setView('verify');
        setSuccess(
          err.codeResent
            ? "You already started signing up. We've sent a new code to " + email.trim() + '.'
            : 'You already started signing up. Enter the code we sent you, or request a new one.'
        );
      } else if (err instanceof SignupConflictError) {
        // A usable account exists. Offer the password reset here, because
        // this is the case where it actually works.
        setError('You already have an account with this email. Sign in below, or reset your password.');
        setView('password');
        setMode('signin');
      } else {
        setError((err as Error)?.message || 'Sign up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedCode = code.replace(/\D/g, '');
    if (trimmedCode.length !== 6) return setError('Please enter the 6-digit code.');
    setLoading(true);
    clearMessages();
    try {
      const data = await verifyEmail({ email: email.trim(), code: trimmedCode });
      if (pendingCreds?.email && pendingCreds?.password) {
        try {
          setSuccess('Email verified. Signing you in…');
          await loginWithEmailAction({ email: pendingCreds.email, password: pendingCreds.password });
        } catch {
          setSuccess('Verified. Please sign in.');
          setTimeout(() => resetToEmail('signin'), 1500);
        }
        return;
      }
      const token = data?.token ?? data?.access_token;
      if (token) {
        setSuccess(data?.message || 'Email verified. Signing you in…');
        completeAuth({ token, idToken: data?.idToken ?? null, refreshToken: data?.refreshToken ?? null }, email.trim());
        return;
      }
      setSuccess(data?.message || 'Email verified. Please sign in.');
      setTimeout(() => resetToEmail('signin'), 1500);
    } catch (err) {
      setError((err as Error)?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerify = async () => {
    setResendLoading(true);
    clearMessages();
    try {
      const data = await resendVerificationCode({ email: email.trim() });
      setCode('');
      setSuccess(data?.message || 'Verification code sent.');
      setCountdown(60);
    } catch (err) {
      setError((err as Error)?.message || 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotRequest = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return setError('Please enter your email');
    if (!EMAIL_RE.test(trimmed)) return setError('Please enter a valid email');
    setLoading(true);
    clearMessages();
    try {
      await requestForgotPassword({ email: trimmed });
      setEmailForReset(trimmed);
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setCountdown(60);
      setView('confirm');
    } catch (err) {
      setError((err as Error)?.message || 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedCode = code.trim();
    if (!trimmedCode) return setError('Please enter the verification code');
    const validation = validateSignupPassword(newPassword);
    if (!validation.valid) return setError(validation.message);
    if (newPassword !== confirmPassword) return setError('New password and confirmation do not match');
    setLoading(true);
    clearMessages();
    try {
      await confirmForgotPassword({ email: emailForReset, code: trimmedCode, newPassword });
      setSuccess('Password reset. You can sign in now.');
      setTimeout(() => {
        resetToEmail('signin');
        setEmail(emailForReset);
        setView('password');
      }, 1500);
    } catch (err) {
      setError((err as Error)?.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendReset = async () => {
    if (!emailForReset) return;
    setResendLoading(true);
    clearMessages();
    try {
      await requestForgotPassword({ email: emailForReset });
      setCountdown(60);
      setSuccess('Verification code sent.');
    } catch (err) {
      setError((err as Error)?.message || 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  /* ── render helpers ── */

  const alerts = (
    <>
      {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
      {success && <div className={`${styles.alert} ${styles.alertSuccess}`}>{success}</div>}
    </>
  );

  const titles: Record<string, { title: string; sub: React.ReactNode }> = {
    'signin-email': { title: 'Sign In', sub: 'Sign in to your JurisTab workspace.' },
    'signup-email': { title: 'Sign Up', sub: 'Enter your email to create an account.' },
    'signin-password': { title: 'Sign In', sub: <>Signing in as <strong>{email.trim()}</strong></> },
    'signup-password': { title: 'Sign Up', sub: <>Create a password for <strong>{email.trim()}</strong></> },
    verify: { title: 'Verify your email', sub: <>Enter the 6-digit code we sent to <strong>{email.trim()}</strong></> },
    forgot: { title: 'Forgot password', sub: "Enter your email and we'll send a reset code." },
    confirm: { title: 'Reset password', sub: <>Verification code sent to <strong>{emailForReset}</strong></> },
  };

  const viewKey = view === 'email' || view === 'password' ? `${mode}-${view}` : view;
  const heading = titles[viewKey] || titles['signup-email'];

  const passwordToggle = (show: boolean, setShow: (v: boolean) => void) => (
    <button
      type="button"
      className={styles.eyeBtn}
      onClick={() => setShow(!show)}
      tabIndex={-1}
      aria-label={show ? 'Hide password' : 'Show password'}
    >
      {show ? <EyeOffIcon /> : <EyeIcon />}
    </button>
  );

  const renderBody = () => {
    // Email step (login or signup)
    if (view === 'email') {
      return (
        <form onSubmit={(e) => { e.preventDefault(); handleEmailNext(); }}>
          <div className={styles.field}>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              placeholder="you@chambers.com.au"
              autoComplete="email"
              className={styles.input}
            />
          </div>
          {alerts}
          <button type="submit" className={styles.submit}>
            {mode === 'signin' ? 'Continue with Email' : 'Continue'}
          </button>
          <p className={styles.footer}>
            {mode === 'signin' ? (
              <>Don&apos;t have an account?{' '}
                <button type="button" className={styles.link} onClick={() => resetToEmail('signup')}>Sign up</button>
              </>
            ) : (
              <>Already have an account?{' '}
                <button type="button" className={styles.link} onClick={() => resetToEmail('signin')}>Sign in</button>
              </>
            )}
          </p>
          {mode === 'signin' && (
            <p className={styles.footer} style={{ marginTop: 8 }}>
              <button type="button" className={styles.link} onClick={() => { clearMessages(); setView('forgot'); }}>
                Forgot password?
              </button>
            </p>
          )}
        </form>
      );
    }

    // Password step
    if (view === 'password') {
      const isSignin = mode === 'signin';
      return (
        <>
          <button
            type="button"
            className={styles.backArrow}
            onClick={() => { setPassword(''); clearMessages(); setView('email'); }}
            aria-label="Back to email"
          >
            <ArrowLeftIcon />
          </button>
          <form onSubmit={isSignin ? handleSignin : handleSignup}>
            <div className={styles.field}>
              <div className={styles.inputWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                  placeholder="Password"
                  autoComplete={isSignin ? 'current-password' : 'new-password'}
                  autoFocus
                  className={`${styles.input} ${styles.inputWithIcon}`}
                />
                {passwordToggle(showPassword, setShowPassword)}
              </div>
            </div>
            {alerts}
            <button type="submit" disabled={loading || !!success} className={styles.submit}>
              {isSignin
                ? success ? 'Successfully signed in' : loading ? 'Signing in…' : 'Sign in'
                : loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>
          {isSignin && (
            <p className={styles.footer} style={{ marginTop: 8 }}>
              <button type="button" className={styles.link} onClick={() => { clearMessages(); setView('forgot'); }}>
                Forgot password?
              </button>
            </p>
          )}
        </>
      );
    }

    // Verify email (OTP)
    if (view === 'verify') {
      return (
        <>
          <button
            type="button"
            className={styles.backArrow}
            onClick={() => resetToEmail('signup')}
            aria-label="Back to sign up"
          >
            <ArrowLeftIcon />
          </button>
          <form onSubmit={handleVerify}>
            <OtpInput value={code} onChange={(v) => { setCode(v); if (error) setError(''); if (success) setSuccess(''); }} disabled={loading} />
            {alerts}
            <button type="submit" disabled={loading || code.replace(/\D/g, '').length !== 6} className={styles.submit}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <div className={styles.resendRow}>
              <span className={styles.countdown}>Time remaining: <span className={styles.countdownNum}>{formatCountdown(countdown)}</span></span>
              <button type="button" className={styles.resendBtn} onClick={handleResendVerify} disabled={countdown > 0 || resendLoading}>
                {resendLoading ? 'Sending…' : 'Resend verification code'}
              </button>
            </div>
          </form>
        </>
      );
    }

    // Forgot password (request code)
    if (view === 'forgot') {
      return (
        <form onSubmit={handleForgotRequest}>
          <div className={styles.field}>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              placeholder="Email"
              autoComplete="email"
              autoFocus
              className={styles.input}
            />
          </div>
          {alerts}
          <button type="submit" disabled={loading} className={styles.submit}>
            {loading ? 'Sending…' : 'Send reset code'}
          </button>
          <button type="button" className={styles.backBtn} onClick={() => { clearMessages(); setView('email'); }}>
            Back to sign in
          </button>
        </form>
      );
    }

    // Confirm reset (code + new password)
    return (
      <form onSubmit={handleConfirmReset}>
        <div className={styles.field}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(''); }}
            placeholder="Verification code"
            autoFocus
            className={styles.input}
          />
        </div>
        <div className={styles.field}>
          <div className={styles.inputWrap}>
            <input
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); if (error) setError(''); }}
              placeholder="New password"
              autoComplete="new-password"
              className={`${styles.input} ${styles.inputWithIcon}`}
            />
            {passwordToggle(showNewPassword, setShowNewPassword)}
          </div>
        </div>
        <div className={styles.field}>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(''); }}
            placeholder="Confirm new password"
            autoComplete="new-password"
            className={styles.input}
          />
        </div>
        {alerts}
        <button type="submit" disabled={loading} className={styles.submit}>
          {loading ? 'Resetting…' : 'Reset password'}
        </button>
        <div className={styles.resendRow}>
          <span className={styles.countdown}>Resend code in <span className={styles.countdownNum}>{formatCountdown(countdown)}</span></span>
          <button type="button" className={styles.resendBtn} onClick={handleResendReset} disabled={countdown > 0 || resendLoading}>
            {resendLoading ? 'Sending…' : 'Resend verification code'}
          </button>
        </div>
        <button type="button" className={styles.backBtn} onClick={() => resetToEmail('signin')}>
          Back to sign in
        </button>
      </form>
    );
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={viewKey}
        initial={{ opacity: 0, filter: 'blur(4px)', y: 6 }}
        animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
        exit={{ opacity: 0, filter: 'blur(4px)', y: -6 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className={`${styles.flowContainer}${
          view === 'password' || view === 'verify' ? ` ${styles.flowContainerWithBack}` : ''
        }`}
      >
        <h3 className={styles.flowTitle}>{heading.title}</h3>
        <p className={styles.flowSub}>{heading.sub}</p>
        {renderBody()}
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthFlow;
